import { FLOA_MAX_AMOUNT, FLOA_MIN_AMOUNT, withFieldGroup } from '@massilia/features'
import { cn, Label, RadioGroup, RadioGroupItem } from '@massilia/ui'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import {
  AGENCY_LOCATION_LABEL,
  AGENCY_OPENING_HOURS_LABEL,
  AGENCY_PHONE,
} from '@/lib/constants/agency'

import type { CheckoutFormValues, PaymentMethodFormValue } from '../checkout-form.schema'

import { paymentMethods } from '../checkout-form.schema'
import { BankTransferDetails } from './bank-transfer-details'
import { FloaSubForm } from './floa-sub-form'

interface PaymentMethodSelectorProps {
  isFloaDisabled?: boolean
  isReunion?: boolean
  paymentDeadline?: string
  totalPrice: number
}

function isPaymentMethod(value: string): value is PaymentMethodFormValue {
  return paymentMethods.includes(value as PaymentMethodFormValue)
}

export const PaymentMethodSelector = withFieldGroup({
  defaultValues: {} as Pick<
    CheckoutFormValues,
    | 'billingCity'
    | 'billingCountryCode'
    | 'billingStreet'
    | 'billingZipCode'
    | 'floaPayType'
    | 'paymentMethod'
  >,
  props: {} as PaymentMethodSelectorProps,
  render: function PaymentMethodFields({
    group,
    isFloaDisabled,
    isReunion,
    paymentDeadline,
    totalPrice,
  }) {
    const deadlineFormatted = paymentDeadline
      ? format(paymentDeadline, 'dd MMMM yyyy', { locale: fr })
      : null

    const isFloaEligible = totalPrice >= FLOA_MIN_AMOUNT && totalPrice <= FLOA_MAX_AMOUNT

    return (
      <div className="flex w-full flex-col gap-4">
        <div className="text-gray-light-900 font-semibold">Quel est votre moyen de paiement ?</div>
        <group.AppField name="paymentMethod">
          {(field) => {
            const selected = field.state.value

            return (
              <RadioGroup
                className="flex flex-col gap-0"
                onValueChange={(value) => {
                  if (isPaymentMethod(value) && !(value === 'floa' && isFloaDisabled)) {
                    field.handleChange(value)
                  }
                }}
                value={selected}
              >
                {/* Carte bancaire */}
                <PaymentMethodOption
                  description={
                    <div className="flex flex-col gap-2">
                      <p className="text-gray-light-700 text-sm">
                        Vous serez redirigé vers notre plateforme de paiement sécurisée pour saisir
                        vos informations de carte bancaire.
                      </p>
                      <p className="text-warning-700 text-sm font-medium">
                        Des frais de 2% s&apos;appliqueront à postériori sur les cartes American
                        Express.
                      </p>
                    </div>
                  }
                  id="credit-card"
                  isFirst
                  isSelected={selected === 'credit-card'}
                  label="Carte bancaire"
                  nextIsSelected={selected === 'floa'}
                  value="credit-card"
                />

                {/* Floa */}
                <PaymentMethodOption
                  description={
                    isFloaEligible ? (
                      <FloaSubForm
                        fields={{
                          billingCity: 'billingCity',
                          billingCountryCode: 'billingCountryCode',
                          billingStreet: 'billingStreet',
                          billingZipCode: 'billingZipCode',
                          floaPayType: 'floaPayType',
                        }}
                        form={group}
                      />
                    ) : (
                      <p className="text-gray-light-700 text-sm">
                        Le paiement en 3/4x n&apos;est pas disponible pour ce montant (entre 50€ et
                        6 000€).
                      </p>
                    )
                  }
                  disabled={isFloaDisabled || !isFloaEligible}
                  disabledMessage={
                    isFloaDisabled ? '(Floa indisponible pour ce type de voyage)' : undefined
                  }
                  id="floa"
                  isSelected={!isFloaDisabled && isFloaEligible && selected === 'floa'}
                  label="Floa 3/4x avec frais"
                  prevIsSelected={selected === 'credit-card'}
                  nextIsSelected={selected === 'vacation-cheque'}
                  value="floa"
                />

                {/* Chèque vacances */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-4">
                      {deadlineFormatted && (
                        <div className="flex items-center justify-between">
                          <div className="text-gray-light-700 text-sm font-medium">
                            Date limite de réception des chèques vacances
                          </div>
                          <div className="text-gray-light-900 text-sm font-semibold">
                            {deadlineFormatted}
                          </div>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div className="text-gray-light-900 text-sm font-semibold">
                          Nous les faire parvenir par courrier en recommandé à l&apos;agence.
                        </div>
                        <div className="text-gray-light-900 text-sm">
                          Nous vous informerons des réception du courrier.
                        </div>
                      </div>
                      <div className="text-gray-light-900 text-sm font-semibold">
                        {AGENCY_LOCATION_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="vacation-cheque"
                  isSelected={selected === 'vacation-cheque'}
                  label="Chèque vacances"
                  prevIsSelected={selected === 'floa'}
                  nextIsSelected={selected === 'vacation-e-cheque'}
                  value="vacation-cheque"
                />

                {/* E-chèque vacances */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-4">
                      {deadlineFormatted && (
                        <div className="flex items-center justify-between">
                          <div className="text-gray-light-700 text-sm font-medium">
                            Date limite de réception des E-chèques vacances
                          </div>
                          <div className="text-gray-light-900 text-sm font-semibold">
                            {deadlineFormatted}
                          </div>
                        </div>
                      )}
                      <div className="text-gray-light-900 text-sm font-semibold">
                        Appelez l&apos;agence aux horaires d&apos;ouverture.
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="vacation-e-cheque"
                  isSelected={selected === 'vacation-e-cheque'}
                  label="E-chèque vacances"
                  prevIsSelected={selected === 'vacation-cheque'}
                  nextIsSelected={selected === 'bank-transfer'}
                  value="vacation-e-cheque"
                />

                {/* Virement bancaire */}
                <PaymentMethodOption
                  description={
                    <BankTransferDetails paymentDeadline={paymentDeadline} isReunion={isReunion} />
                  }
                  id="bank-transfer"
                  isSelected={selected === 'bank-transfer'}
                  label="Virement bancaire"
                  prevIsSelected={selected === 'vacation-e-cheque'}
                  nextIsSelected={selected === 'cash'}
                  value="bank-transfer"
                />

                {/* Espèces */}
                <PaymentMethodOption
                  description={
                    <div className="flex w-full flex-col gap-3">
                      <div className="text-gray-light-900 text-sm font-semibold">
                        {AGENCY_LOCATION_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">
                        {AGENCY_OPENING_HOURS_LABEL}
                      </div>
                      <div className="text-gray-light-900 text-sm">Téléphone : {AGENCY_PHONE}</div>
                    </div>
                  }
                  id="cash"
                  isLast
                  isSelected={selected === 'cash'}
                  label="Espèces (uniquement en agence)"
                  prevIsSelected={selected === 'bank-transfer'}
                  value="cash"
                />
              </RadioGroup>
            )
          }}
        </group.AppField>
      </div>
    )
  },
})

interface PaymentMethodOptionProps {
  id: string
  description: React.ReactNode
  disabled?: boolean
  disabledMessage?: string
  isFirst?: boolean
  isLast?: boolean
  isSelected: boolean
  label: string
  nextIsSelected?: boolean
  prevIsSelected?: boolean
  value: string
}

// ── Payment Method Option ──

function PaymentMethodOption({
  id,
  description,
  disabled = false,
  disabledMessage,
  isFirst = false,
  isLast = false,
  isSelected,
  label,
  nextIsSelected = false,
  prevIsSelected = false,
  value,
}: PaymentMethodOptionProps) {
  return (
    <div
      className={cn(
        'border-gray-light-300 border-r border-l transition-all',
        isFirst && 'rounded-t-2xl border-t',
        isLast && 'rounded-b-2xl border-b',
        isSelected && 'border-brand-600 bg-brand-50 border-t border-b',
        !isSelected && prevIsSelected && 'border-t',
        !isSelected && nextIsSelected && 'border-b',
        !isFirst && !isSelected && !prevIsSelected && 'border-t',
        !isLast && !isSelected && !nextIsSelected && 'border-b',
        disabled && 'opacity-50'
      )}
    >
      <div
        className={cn(
          'flex items-start gap-2 p-4',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <RadioGroupItem className="mt-0.5" disabled={disabled} id={id} value={value} />
        <Label
          className={cn(
            'flex-1 text-sm font-semibold',
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          )}
          htmlFor={id}
        >
          {label}
          {disabled && disabledMessage && (
            <span className="text-gray-light-500 ml-2 text-xs font-normal">{disabledMessage}</span>
          )}
        </Label>
      </div>
      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isSelected ? 'max-h-150 opacity-100' : 'pointer-events-none max-h-0 opacity-0'
        )}
      >
        <div className="px-4 pb-4">{description}</div>
      </div>
      {/* Show ineligibility message when not selected but disabled */}
      {disabled && !isSelected && (
        <div className="px-4 pb-3">
          <p className="text-gray-light-500 text-xs">
            Le paiement en 3/4x n&apos;est pas disponible pour ce montant (entre 50€ et 6 000€).
          </p>
        </div>
      )}
    </div>
  )
}
